// Importing necessary libraries and components.
import React, { useState, useEffect, useContext } from 'react';
import Header from './payment_request/Header';
import ChargesGrid from './payment_request/ChargeGrid';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '@mui/material/Button';
import logo from '../wisdom.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileUploadComponent from '../utils/FileUploadComponent'
import { faSort, faFileAlt, faExternalLinkAlt, faArrowDown, faCheckCircle, faMoneyBillWave, faUser, faCoins, faTag, faCalculator, faReceipt, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { DataContext } from '../contexts/DataContext';

// Sample initial data 
const initialHeaderData = {
  requestNumber: '12345',
  reference: 'REF123',
  customer: 'John Doe',
  containerData: 'Container123',
  date: '09/29/2023',
  pedimento: 'PED123',
};

const initialChargesData = {
  tax_expenses: [],
  actual_expenses: [],
  custom_brokerage_expenses: []
};

// Main PaymentAdvance component
const PaymentRequest = () => {
  const location = useLocation();
  const passedRowData = location.state?.rowData || {};
  const navigate = useNavigate();
  const paymentRequestId = location.state?.paymentRequestId;
  const { triggerRefetch } = useContext(DataContext);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState({});
  const [priority, setPriority] = useState('Normal');
  const [billTo, setBillTo] = useState('Cliente');
  const [currency, setCurrency] = useState('MXP');
  const [supplier, setSupplier] = useState(null);
  const [supplierName, setSupplierName] = useState(null);

  const formatAmount = (value) => {
    if (!value) return "$0.00"; // Return "$0.00" if there's no value

    // Parse the value to a float to handle if it's already a number or string
    let number = parseFloat(value);
    if (isNaN(number)) return "$0.00"; // Return "$0.00" if the parsed value is not a valid number

    // Format the number as currency
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };


  const [IVA, setIVA] = useState(formatAmount(0));
  const [commission, setCommission] = useState(formatAmount(0));

  const [totalToPay, setTotalToPay] = useState(0); // Total to pay
  const [subtotal, setSubtotal] = useState(0); // Total to pay

  //Files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesList, setFilesList] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'uploaded', direction: 'ascending' });

  const handleIvaInputChange = (event) => {
    const input = event.target.value.replace(/[^\d.]/g, ''); // Allow only numbers and dot
    setIVA(input);
  };

  const handleCommissionInputChange = (event) => {
    const input = event.target.value.replace(/[^\d.]/g, ''); // Allow only numbers and dot
    setCommission(input);
  };

  // Enhanced formatting function for display
  const formatForDisplay = (value) => {
    // Early return for empty string to avoid formatting issues
    if (value === "") return "";

    const number = Number(value);
    if (isNaN(number)) return "";

    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleDeleteFile = async (fileId) => {
    // Assume an endpoint for deleting files by ID
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/${fileId}`);
      // Update local state to remove the file from the list
      const updatedFilesList = filesList.filter(file => file.id !== fileId);
      setFilesList(updatedFilesList);
      alert("File deleted successfully.");
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete the file.");
    }
  };

  const handleSort = (key) => {
    sortFiles(key);
  };

  useEffect(() => {
    const fetchFilesForEntity = async () => {
      try {
        const entityType = "PaymentRequest";
        if (paymentRequestId && entityType) {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/entity/${paymentRequestId}?entityType=${entityType}`);
          setFilesList(response.data.files);
        }
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };

    fetchFilesForEntity();
  }, [paymentRequestId]);

  useEffect(() => {
    if (paymentRequestId) {
      // Fetch and set existing payment advance data
      axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/${paymentRequestId}`)
        .then(response => {
          const data = response.data;
          setHeaderData({
            requestNumber: data._id,
            reference: passedRowData.traffic_reference,
            customer: passedRowData.customer_data?.customer_name,
            customer_key: passedRowData.customer_data?.customer_key,
            customer_rfc: passedRowData.customer_data?.customer_rfc,
            container_number: passedRowData.container_data?.container_number,
            container_type: passedRowData.container_data?.container_type,
            container_size: passedRowData.container_data?.container_size,
            pedimento: passedRowData.pedimento,
            tipo_pedimento: passedRowData.tipo_pedimento,
            status_pedimento: passedRowData.status_pedimento,
            status_modulacion: passedRowData.status_modulacion,
            number_of_reference_invoices: passedRowData.number_of_reference_invoices,
            status: data.status
          });
          setExpenses({
            arrival_expenses: data.arrival_expenses,
            dispatch_expenses: data.dispatch_expenses,
            operation_closure_expenses: data.operation_closure_expenses
          });
          setIVA(data.total_iva.toString());
          setCommission(data.total_comission.toString());
          setTotalToPay(data.total_to_pay);
          setPriority(data.priority);
          setSupplier(data.supplier);
          setSupplierName(data.supplier_name);
          setCurrency(data.currency);
          setBillTo(data.bill_to)
        })
        .catch(error => console.error('Error fetching payment request data:', error));
    }
  }, [paymentRequestId]);

  const getPresignedUrlAndUpload = async (file) => {
    console.log('File to upload:', file.name, file.type);

    try {
      // Ensure file name and type are defined
      if (!file.name || !file.type) {
        throw new Error('File name or type is undefined.');
      }

      // Step 1: Request a presigned URL from your backend
      const queryParams = new URLSearchParams({
        filename: file.name,
        filetype: file.type,
      });
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/get-presigned-url?${queryParams.toString()}`, {
        method: 'GET',
        // Do not include default headers or credentials
      });

      if (!response.ok) {
        throw new Error('Failed to get presigned URL from backend');
      }
      const { url } = await response.json();

      // Step 2: Use the presigned URL to upload the file directly to S3
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file, // Directly use the file Blob
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Return the file metadata after successful upload
      const fileUrl = url.split('?')[0];
      return {
        filename: file.name,
        s3Key: url.split('/')[url.split('/').length - 1].split('?')[0], // Extract key from URL
        s3Url: fileUrl,
      };
    } catch (error) {
      console.error('Error getting presigned URL or uploading file:', error);
      throw error; // Rethrow the error to be handled in the calling function
    }
  };

  const sortFiles = (key) => {
    let direction = 'ascending';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleViewFile = async (fileId) => {
    try {
      const url = await fetchPresignedUrl(fileId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening file:', error);
    }
  };

  const handleDownloadFile = async (fileId) => {
    try {
      // Fetch the presigned URL for downloading
      const presignedUrl = await fetchPresignedUrl(fileId);

      // Create a temporary anchor (<a>) tag to programmatically click for download
      const downloadLink = document.createElement('a');
      downloadLink.href = presignedUrl;
      downloadLink.setAttribute('download', true); // Optional: you can set the filename using download attribute
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download the file.');
    }
  };


  const fetchPresignedUrl = async (fileId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/${fileId}`);
      if (!response.ok) throw new Error('Failed to fetch pre-signed URL');
      const data = await response.json();
      return data.fileUrl; // Assuming the backend returns { fileUrl: '...' }
    } catch (error) {
      console.error('Error fetching pre-signed URL:', error);
      throw error;
    }
  };

  const onFilesSelected = (filesWithClassification) => {
    setSelectedFiles(filesWithClassification); // This now contains objects with both file and classification
  };

  const validateData = () => {
    // Validate header data
    if (!headerData.reference || !headerData.customer || !headerData.pedimento) {
      return { success: false, message: "Header information (reference, customer, or pedimento) is incomplete." };
    }

    // Validate expenses
    for (let category of ["arrival_expenses", "dispatch_expenses", "operation_closure_expenses"]) {
      if (!Array.isArray(expenses[category])) {
        return { success: false, message: `Expected ${category} to be an array.` };
      }
      for (let expense of expenses[category]) {
        if (!expense.concept || !expense.amount || parseFloat(expense.amount) === 0) {
          return { success: false, message: `One or more expenses in ${category} are missing a concept, amount, or have an amount of 0.` };
        }
      }
    }

    // Validate additional fields
    if (!priority) {
      return { success: false, message: "Priority field is empty." };
    }

    if (!billTo) {
      return { success: false, message: "Bill To field is empty." };
    }

    if (!currency) {
      return { success: false, message: "Currency field is empty." };
    }

    if (!supplier) {
      return { success: false, message: "Supplier field is not selected." };
    }

    // If all validations pass
    return { success: true, message: "All validations passed." };
  };


  // State for managing header and charges
  // Populating header data with passed data
  const [headerData, setHeaderData] = useState({
    ...initialHeaderData,
    reference: passedRowData.traffic_reference,
    customer: passedRowData.customer_data?.customer_name,
    customer_key: passedRowData.customer_data?.customer_key,
    customer_rfc: passedRowData.customer_data?.customer_rfc,
    container_number: passedRowData.container_data?.container_number,
    container_type: passedRowData.container_data?.container_type,
    container_size: passedRowData.container_data?.container_size,
    pedimento: passedRowData.pedimento,
    tipo_pedimento: passedRowData.tipo_pedimento,
    status_pedimento: passedRowData.status_pedimento,
    status_modulacion: passedRowData.status_modulacion,
    number_of_reference_invoices: passedRowData.number_of_reference_invoices
  });

  const [expenses, setExpenses] = useState(initialChargesData);

  const handleSavePaymentRequest = async () => {
    // Check if all required fields are filled out
    const validationResult = validateData();
    if (!validationResult.success) {
      alert(validationResult.message); // Display the specific failure reason
      return;
    }

    //FILE Validation and status management
    const combinedFiles = [...filesList, ...selectedFiles.map(file => ({ ...file.file, category: file.classification }))];

    // Ensure there are no unclassified files when creating a new PaymentAdvance or editing an existing one
    const hasUnclassifiedFile = selectedFiles.some(file => file.classification === 'UNCLASSIFIED');
    if (hasUnclassifiedFile) {
      alert("All files must have a valid classification before saving.");
      return;
    }

    // Ensure there's at least one file for new PaymentRequest or files are already uploaded for an existing PaymentAdvance
    if (!paymentRequestId && selectedFiles.length === 0) {
      alert("Please upload at least one file before saving.");
      return;
    }

    if (paymentRequestId && filesList.length === 0 && selectedFiles.length === 0) {
      alert("At least one file must be associated with the Payment Request record.");
      return;
    }

    const hasComprobanteDePago = combinedFiles.some(file => file.category === 'COMPROBANTE DE PAGO');
    const hasFacturaPDF = combinedFiles.some(file => file.category === 'FACTURA PDF');
    const hasFacturaXML = combinedFiles.some(file => file.category === 'FACTURA XML');
    const shouldCloseRequest = hasFacturaPDF && hasFacturaXML && hasComprobanteDePago;

    let status;
    if (paymentRequestId) {
      if (shouldCloseRequest) {
        status = 'closed';
      } else {
        status = hasComprobanteDePago ? 'payment_executed' : 'open';
      }
    } else {
      if (shouldCloseRequest) {
        status = 'closed';
      } else {
        status = hasComprobanteDePago ? 'payment_executed' : 'open';
      }
    }

    let currentPaymentRequestId = paymentRequestId;


    // Prepare the data for saving
    const paymentRequestData = {
      traffic_reference: headerData.reference,
      entry_point: headerData.entry_point,
      license: headerData.license,
      pedimento: headerData.pedimento,
      tipo_pedimento: headerData.tipo_pedimento,
      status_pedimento: headerData.status_pedimento,
      status_modulacion: headerData.status_modulacion,
      number_of_reference_invoices: headerData.number_of_reference_invoices,
      customer: headerData.customer,
      customer_data: {
        customer_key: headerData.customer_key,
        customer_name: headerData.customer,
        customer_rfc: headerData.customer_rfc,
      },
      container_data: {
        container_number: headerData.container_number,
        container_type: headerData.container_type,
        container_size: headerData.container_size,
      },
      total_requested_payment: 0,
      actual_payment: 0,
      total_arrival_expenses: 0,
      total_dispatch_expenses: 0,
      total_operation_closure_expenses: 0,
      arrival_expenses: expenses.arrival_expenses || [],
      dispatch_expenses: expenses.dispatch_expenses || [],
      operation_closure_expenses: expenses.operation_closure_expenses || [],
      priority: priority,
      supplier: supplier,
      supplier_name: supplierName,
      subtotal: subtotal,
      total_iva: IVA,
      total_comission: commission,
      total_to_pay: totalToPay,
      currency: currency,
      bill_to: billTo,
      status: status,
    };

    try {
      let response;
      if (!currentPaymentRequestId) {
        // If creating a new payment request
        response = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/create`, paymentRequestData);
        currentPaymentRequestId = response.data._id; // Update local variable with new ID
      } else {
        // If updating an existing payment request
        response = await axios.put(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/${currentPaymentRequestId}`, paymentRequestData);
      }

      // Save the payment request data to your backend
      //const saveResponse = await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/create`, paymentRequestData);
      const savedPaymentRequestId = currentPaymentRequestId; // Assuming the backend returns the ID of the saved request

      // Handle file uploads after the payment request has been successfully saved
      if (selectedFiles.length > 0) { //If we have files in the drop

        const uploadedFilesMetadata = await Promise.all(selectedFiles.map(async (fileWithClassification) => {
          const metadata = await getPresignedUrlAndUpload(fileWithClassification.file);
          return {
            filename: metadata.filename,
            s3Key: metadata.s3Key,
            s3Url: metadata.s3Url,
            category: fileWithClassification.classification,
            entityId: savedPaymentRequestId,
            entityType: 'PaymentRequest',
          };
        }));

        // Save the file metadata to your backend
        await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/api/files/saveMetadata`, {
          files: uploadedFilesMetadata,
          paymentRequestId: savedPaymentRequestId,
        });
        setUploadedFiles(uploadedFilesMetadata);
      }

      const sicaPayload = {
        ...paymentRequestData, // Spread existing paymentRequestData to include all previously set fields
        paymentRequestId: savedPaymentRequestId, // Add the currentPaymentRequestId to the payload
      };

      //Logic for inserting to SICA
      // Now, call the new endpoint to insert accountability data
      let endpoint;
      let method;
      // Determine if this is a new insert or an update based on the presence of paymentRequestId
      if (!paymentRequestId) {
        // If paymentRequestId does not exist, it's an insert
        endpoint = `${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/insertPreconta`;
        method = 'post'; // Use post for insert
      } else {
        // If paymentRequestId exists, it's an update
        endpoint = `${process.env.REACT_APP_BACKEND_API_URL}/api/payment_request/updatePreconta/${paymentRequestId}`;
        method = 'put'; // Use put for update
      }

      // Send the request to the determined endpoint using the determined method
      const responseSica = await axios[method](endpoint, sicaPayload);

      // Update UI to reflect the saved state
      setModalData(response.data);
      setIsModalVisible(true);
    } catch (error) {
      console.error("Error saving the payment request or uploading files:", error);
      alert("There was an error saving the payment request. Please try again.");
    }
  };

  useEffect(() => {
    if (!expenses || !expenses.arrival_expenses || !expenses.dispatch_expenses || !expenses.operation_closure_expenses) {
      return; // Skip the useEffect logic if expenses are not ready
    }
    // Sum up the amounts for each category of expenses directly from the expenses variable
    const subtotal = ['arrival_expenses', 'dispatch_expenses', 'operation_closure_expenses'].reduce((total, category) => {
      return total + (expenses[category] || []).reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
    }, 0);

    // Calculate the total to pay by adding IVA and commission to the subtotal
    const total = subtotal + (Number(IVA) || 0) + (Number(commission) || 0);
    setTotalToPay(total);
    setSubtotal(subtotal);
  }, [IVA, commission, expenses]);

  const formatCurrency = (value) => {
    // Convert input to a number to handle strings and ensure correct formatting
    const numericValue = Number(value);
    // Format the number as currency with 2 decimal places, adding a $ sign
    return numericValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  return (
    <>
      <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
        <div className="flex items-center">
          <img src={logo} alt="Wisdom Logo" className="h-12 mr-2" />
          <span className="text-xl font-semibold text-gray-700"></span>
        </div>
        <div className="text-xl font-semibold text-gray-700">
          Payment Request
        </div>
      </nav>

      <div className="min-h-screen bg-gray-100 p-6">
        {/* Header Component */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-6">
          <Header data={headerData} requestNumber={headerData.requestNumber} />
        </div>

        {/* ChargesGrid Component */}
        <div className="flex">
          <div className="flex-1">
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-6">
              <ChargesGrid expenses={expenses} setExpenses={setExpenses} setPriority={setPriority} priority={"Normal"} setBillTo={setBillTo} billTo={billTo} setCurrency={setCurrency} currency={currency} setSupplier={setSupplier} supplier={supplier} setSupplierName={setSupplierName} supplierName={supplierName} />
              <div className="mt-6">
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="subtotal">
                    Subtotal
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="subtotal"
                    type="text"
                    placeholder="Subtotal"
                    value={formatAmount(
                      ['arrival_expenses', 'dispatch_expenses', 'operation_closure_expenses']
                        .reduce((total, category) => {
                          return total + (expenses[category] || []).reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
                        }, 0)
                    )}
                    readOnly
                  />

                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="IVA">
                    IVA
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="IVA"
                    type="text"
                    placeholder="$0.00"
                    value={IVA}
                    onChange={handleIvaInputChange}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="commission">
                    Commission
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="commission"
                    type="text"
                    placeholder="$0.00"
                    value={commission}
                    onChange={handleCommissionInputChange}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="totalToPay">
                    Total to Pay
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="totalToPay"
                    type="text"
                    placeholder="Total to Pay"
                    value={formatAmount(totalToPay)}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <button
                className="bg-blue-900 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded"
                onClick={handleSavePaymentRequest}
              >
                Save Payment Request
              </button>
            </div>
          </div>
          {/* Column 2: New file list and upload components */}
          <div className="flex-1 ml-4">
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-6">
              <h2 className="text-xl font-semibold mb-4">Files</h2>
              {/* Separate container for headers */}
              <div className="grid grid-cols-5 text-gray-600 cursor-pointer sticky top-0 bg-white p-2 z-10">
                <p className="text-center" onClick={() => handleSort('filename')}>Name <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('size')}>Size <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('uploadDate')}>Uploaded <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center" onClick={() => handleSort('category')}>Type <FontAwesomeIcon icon={faSort} /></p>
                <p className="text-center">Action</p>
              </div>
              {/* Scrollable container for file list */}
              <div className="overflow-y-auto h-64">
                {/* File List Content */}
                {filesList.map((file, index) => (
                  <div key={index} className="grid grid-cols-5 items-center bg-gray-100 p-2 rounded-md mb-2 last:mb-0">
                    <p className="truncate col-span-1" title={file.filename}>{file.filename.substring(0, 20)}{file.filename.length > 20 ? '...' : ''}</p>
                    <p className="text-center col-span-1">{isNaN(file.size) ? '—' : `${(file.size / 1024).toFixed(2)} KB`}</p>
                    <p className="text-center col-span-1">{new Date(file.uploadDate).toLocaleDateString()}</p>
                    <div className="col-span-1">
                      <span className="px-3 py-1 text-green-800 text-xs font-medium bg-green-100 rounded-full text-center block">
                        {file.category}
                      </span>
                    </div>
                    <div className="flex justify-center col-span-1 space-x-2">
                      <button onClick={() => handleViewFile(file._id)} className="text-blue-600 hover:text-blue-800">
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="lg" />
                      </button>
                      {/*
                      <button onClick={() => handleDownloadFile(file._id)} className="text-blue-600 hover:text-blue-800">
                        <FontAwesomeIcon icon={faArrowDown} size="lg" />
                      </button>
                      */}
                    </div>
                  </div>
                ))}
              </div>
            </div>



            {/* File Upload Component */}
            <div className="bg-white shadow rounded-lg overflow-hidden p-6">
              <FileUploadComponent onFilesSelected={onFilesSelected} usageContext={"PaymentRequest"} />
            </div>
          </div>
        </div>

        {/* Modal JSX */}
        {isModalVisible && (
          <div className="fixed inset-0 z-10 overflow-y-auto bg-black bg-opacity-60">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="w-full max-w-4xl max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-2xl border-t-4 border-blue-900 p-8">
                <h2 className="mb-4 text-3xl font-bold text-indigo-600 flex items-center justify-start">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-2" /> Payment Request Created Successfully!
                </h2>
                <div className="mb-6">
                  <p className="text-xl"><strong>Request Number:</strong> {modalData._id}</p>
                </div>
                <hr className="my-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-lg"><FontAwesomeIcon icon={faTag} className="mr-2" /><strong>Priority:</strong> {priority}</p>
                    <p className="text-lg"><FontAwesomeIcon icon={faUser} className="mr-2" /><strong>Bill To:</strong> {billTo}</p>
                    <p className="text-lg"><FontAwesomeIcon icon={faCoins} className="mr-2" /><strong>Currency:</strong> {currency}</p>
                    <p className="text-lg"><FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" /><strong>Supplier:</strong> {supplier || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-lg"><FontAwesomeIcon icon={faCalculator} className="mr-2" /><strong>IVA:</strong> {formatCurrency(IVA)}</p>
                    <p className="text-lg"><FontAwesomeIcon icon={faReceipt} className="mr-2" /><strong>Commission:</strong> {formatCurrency(commission)}</p>
                  </div>
                </div>
                <hr className="my-4" />
                <div className="mb-6">
                  <p className="text-2xl font-bold"><FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" /><strong>Subtotal:</strong> {formatCurrency(subtotal)}</p>
                  <p className="text-2xl font-bold"><FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-2" /><strong>Total to Pay:</strong> {formatCurrency(totalToPay)}</p>
                </div>
                <hr className="my-4" />
                {/* Expense Categories */}
                <div className="mb-6">
                  <p className="text-lg font-bold mb-2">Expense Details:</p>
                  {/* Include expenses details here */}
                </div>
                <button
                  onClick={() => {
                    setIsModalVisible(false);

                    triggerRefetch(); //Used to correctly trigger DataContext callBacks

                    navigate("/trafficDataGrid");
                  }}
                  className="px-6 py-3 text-lg font-medium text-white bg-blue-900 border border-blue-900 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}



      </div>
    </>
  );

};

export default PaymentRequest;
